import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import { displayWithCurrency } from '../../../utility/currency'
import { formatPaymentMethodDisplay } from '../../../utility/enrollmentsBilling'
import Modal from '../../common/Modal'
import SubmitButton from '../../common/buttons/SubmitButton'
import ProfileAvatar from '../../common/avatar/ProfileAvatar'

import classes from './TransferConfirmationModal.module.scss'

// Transfer flow confirmation step (Figma 2749-2905). Shown after the coach picks the target
// program: capture a custom family message + send-email choice, and review the billing that
// carries over. onConfirm receives { coachMessage, sendEmail }.
const TransferConfirmationModal = ({ isOpen, onClose, enrollment, onConfirm, isLoading }) => {
  const [coachMessage, setCoachMessage] = useState('')
  const [sendEmail, setSendEmail] = useState(true)

  const course = enrollment?.course
  const invoice = enrollment?.invoice
  const student = enrollment?.student
  const pkg = enrollment?.package
  // R2 has no invoice yet, so fall back to the enrollment's own currency/amount (transactionAmount
  // already resolves to the package price when no explicit amount is set).
  const currency = invoice?.currencyCode ? { code: invoice.currencyCode } : enrollment?.currency
  const familyAdminEmail = student?.familyAdmins?.[0]?.email

  const money = amount => (currency ? displayWithCurrency(amount, currency) : `$${Number(amount || 0).toFixed(2)}`)
  const total = Number(invoice?.total ?? enrollment?.transactionAmount ?? 0)

  const programDates = course?.startDate
    ? `${moment(course.startDate).format('MM/DD/YYYY')}${course.endDate ? `-${moment(course.endDate).format('MM/DD/YYYY')}` : ''}`
    : '-'

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={classes.modal}>
      <div className={classes.layout}>
        {/* Left: custom message + send email */}
        <div className={classes.form}>
          <h2 className={classes.heading}>Transfer</h2>

          <span className={classes.label}>Add Custom Message</span>
          <textarea
            className={classes.textarea}
            value={coachMessage}
            onChange={e => setCoachMessage(e.target.value)}
            placeholder='Write a custom message for the family! (optional)'
          />

          <label className={classes.checkRow}>
            <input className={classes.nativeInput} type='checkbox' checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
            <span className={classes.checkbox} />
            <span>Send as Email</span>
          </label>
        </div>

        {/* Right: billing card */}
        <div className={classes.preview}>
          <div className={classes.invoiceCard}>
            <div className={classes.studentRow}>
              <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
              <span className={classes.studentName}>{student?.fullName || `${student?.firstName || ''} ${student?.lastName || ''}`}</span>
            </div>

            <span className={classes.sectionTitle}>Billing Details</span>

            <div className={classes.detailRows}>
              <Row label='Program' value={course?.name || '-'} />
              <Row label='Facility' value={course?.communityName || '-'} />
              <Row label='Coach' value={course?.coachName || '-'} />
              {familyAdminEmail && <Row label='Family Admin Email' value={familyAdminEmail} />}
              <Row label='Program Dates' value={programDates} />
              {invoice?.paymentMethod && (
                <Row label='Payment Method' value={formatPaymentMethodDisplay(invoice)} valueClass={classes.paymentMethodValue} />
              )}
            </div>

            {pkg?.name && (
              <div className={classes.amountsBox}>
                <Row label={pkg.name} value={money(pkg.price ?? total)} boldLabel />
              </div>
            )}

            <div className={classes.totalRow}>
              <span>Total</span>
              <span>{money(total)}</span>
            </div>

            <span className={classes.sectionTitle}>Additional Notes</span>
            <p className={classNames(classes.notesText, { [classes.notesPlaceholder]: !coachMessage })}>
              {coachMessage || 'Custom message for the family'}
            </p>
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <SubmitButton
          buttonMessage='Confirm Changes'
          buttonColor='orange-button'
          className={classes.submitButton}
          loading={isLoading}
          disabled={isLoading}
          onClick={() => onConfirm({ coachMessage, sendEmail })}
        />
      </div>
    </Modal>
  )
}

const Row = ({ label, value, valueClass, boldLabel }) => (
  <div className={classes.detailRow}>
    <span className={classNames(classes.detailLabel, { [classes.boldLabel]: boldLabel })}>{label}</span>
    <span className={classNames(classes.detailValue, valueClass)}>{value}</span>
  </div>
)

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  valueClass: PropTypes.string,
  boldLabel: PropTypes.bool,
}

TransferConfirmationModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  enrollment: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
}

export default TransferConfirmationModal
